import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SmartDefaultsBanner from './SmartDefaultsBanner';
import EditDefaultsModal from './EditDefaultsModal';
import HeadsUpModal from './HeadsUpModal';
import { useSmartDefaults } from './useSmartDefaults';
import { fetchDefaultValueCounts } from './productDefaultSettingsApi';
import { countHandEditedProducts } from './utils';
import { OPEN_EDIT_DEFAULTS_PARAM } from '../../../../../utils/smartDefaults';

const SmartDefaultsModule = (props) => {
    const { products = [], canEdit = true, userData, onDefaultsSaved } = props;
    const { defaults, saveDefaults } = useSmartDefaults(userData?.token);
    const [searchParams, setSearchParams] = useSearchParams();
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [headsUpOpen, setHeadsUpOpen] = useState(false);
    const [pendingDefaults, setPendingDefaults] = useState(null);
    const [editedCount, setEditedCount] = useState(0);
    const [saving, setSaving] = useState(false);

    const blurActiveElement = () => {
        document.activeElement?.blur?.();
    };

    const closeEditModal = () => {
        blurActiveElement();
        setEditModalOpen(false);
    };

    const handleEditClick = () => {
        if (!canEdit) return;
        setEditModalOpen(true);
    };

    useEffect(() => {
        if (searchParams.get(OPEN_EDIT_DEFAULTS_PARAM) !== '1') {
            return;
        }

        if (canEdit) {
            setEditModalOpen(true);
        }

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete(OPEN_EDIT_DEFAULTS_PARAM);
        setSearchParams(nextParams, { replace: true });
    }, [canEdit, searchParams, setSearchParams]);

    const handleSaveResult = (result, { closeEditModal = false } = {}) => {
        if (result?.success) {
            onDefaultsSaved?.(result);
            if (closeEditModal) {
                setEditModalOpen(false);
            }
            return true;
        }

        return false;
    };

    const resolveEditedCount = async () => {
        const countsResult = await fetchDefaultValueCounts(userData?.token);
        if (countsResult.success) {
            return countsResult.editedCount;
        }

        return countHandEditedProducts(products, defaults);
    };

    const handleEditSave = async (draftDefaults) => {
        const hasChanges = JSON.stringify(draftDefaults) !== JSON.stringify(defaults);

        if (!hasChanges) {
            closeEditModal();
            return;
        }

        setSaving(true);
        try {
            const nextEditedCount = await resolveEditedCount();

            setEditedCount(nextEditedCount);
            setPendingDefaults(draftDefaults);
            setEditModalOpen(false);
            setHeadsUpOpen(true);
        } finally {
            setSaving(false);
        }
    };

    const handleHeadsUpChoice = async (overwriteAll) => {
        if (!pendingDefaults) return;
        setSaving(true);
        try {
            const result = await saveDefaults(pendingDefaults, { overwriteAll });
            if (handleSaveResult(result)) {
                setHeadsUpOpen(false);
                setPendingDefaults(null);
                setEditedCount(0);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleHeadsUpCancel = () => {
        setHeadsUpOpen(false);
        setPendingDefaults(null);
        setEditedCount(0);
        setEditModalOpen(true);
    };

    return (
        <>
            <SmartDefaultsBanner
                defaults={defaults}
                onEditClick={handleEditClick}
                canEdit={canEdit}
            />
            <EditDefaultsModal
                open={editModalOpen}
                defaults={defaults}
                saving={saving}
                onCancel={closeEditModal}
                onSave={handleEditSave}
            />
            <HeadsUpModal
                open={headsUpOpen}
                handEditedCount={editedCount}
                saving={saving}
                onCancel={handleHeadsUpCancel}
                onConfirm={handleHeadsUpChoice}
            />
        </>
    );
};

export default SmartDefaultsModule;
